import { useEffect, useRef, useState } from "react";
import uvedImg from "../../assets/uved.png";
import policiaImg from "../../assets/policia.png";
import fullscreenEnterIcon from "../../assets/full-screen.png";
import fullscreenExitIcon from "../../assets/full-screen-exit.png";

const MAX_RECONNECT_ATTEMPTS = 5;
const DISCONNECTED_WAIT_MS   = 8000; // tiempo antes de reconectar en disconnected

export default function DroneViewer({ whepUrl, streamToken, getStreamToken }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [kicked, setKicked]           = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectCount, setReconnectCount] = useState(0);

  const reconnectTimerRef    = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const userName = localStorage.getItem("fullname");

  // Detectar fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // WebRTC
  useEffect(() => {
    if (!streamToken && !getStreamToken) return;
    if (!videoRef.current) return;

    let pc;
    const videoEl = videoRef.current;

    const scheduleReconnect = (delayMs) => {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setReconnecting(false);
          setKicked(true);
          return;
        }
        reconnectAttemptsRef.current += 1;
        setReconnectCount(c => c + 1);
      }, delayMs);
    };

    const run = async () => {
      setLoading(true);
      setError(null);
      setKicked(false);
      setReconnecting(false);

      let token = streamToken;
      if (getStreamToken) {
        try {
          token = await getStreamToken();
        } catch {
          setError("No se pudo obtener acceso al streaming");
          setLoading(false);
          return;
        }
      }

      try {
        pc = new RTCPeerConnection({
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
        });

        pc.addTransceiver("video", { direction: "recvonly" });

        pc.ontrack = (event) => {
          videoEl.srcObject = event.streams[0];

          let dismissed = false;
          const dismiss = () => {
            if (dismissed) return;
            dismissed = true;
            setLoading(false);
            setReconnecting(false);
            reconnectAttemptsRef.current = 0;
          };

          // loadeddata fires when the first frame is decoded regardless of play() result
          // playing fires when autoplay is allowed and playback actually starts
          videoEl.addEventListener('loadeddata', dismiss, { once: true });
          videoEl.addEventListener('playing', dismiss, { once: true });

          videoEl.play().catch(err => console.error('Autoplay blocked:', err));
        };

        pc.onconnectionstatechange = () => {
          const state = pc.connectionState;

          if (state === "connected") {
            clearTimeout(reconnectTimerRef.current);
            setReconnecting(false);
            reconnectAttemptsRef.current = 0;

          } else if (state === "disconnected") {
            // Esperar antes de reconectar; puede recuperarse solo
            setReconnecting(true);
            scheduleReconnect(DISCONNECTED_WAIT_MS);

          } else if (state === "failed") {
            // Fallo definitivo de ICE: reconectar sin espera
            setReconnecting(true);
            scheduleReconnect(0);

          } else if (state === "closed") {
            clearTimeout(reconnectTimerRef.current);
            videoEl.srcObject = null;
            setReconnecting(false);
            setKicked(true);
            setLoading(false);
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const resp = await fetch(`${whepUrl}?t=${token}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/sdp",
            "Accept": "application/sdp"
          },
          body: offer.sdp,
        });

        if (!resp.ok) {
          if (resp.status === 404) {
            setError("El dron no está emitiendo");
            setLoading(false);
            setTimeout(() => run(), 10000);
          } else if (resp.status === 403) {
            setError("No tiene permisos para ver este dron");
            setLoading(false);
          } else {
            setError("Error al conectar con el dron");
            setLoading(false);
          }
          console.error("Error WHEP:", resp.status, await resp.text());
          return;
        }

        const answer = await resp.text();
        await pc.setRemoteDescription({ type: "answer", sdp: answer });

      } catch (err) {
        setError("Error de conexión");
        setLoading(false);
        console.error(err);
      }
    };

    run();

    return () => {
      clearTimeout(reconnectTimerRef.current);
      if (pc) pc.close();
      if (videoEl) videoEl.srcObject = null;
      setLoading(true);
      setError(null);
    };
  }, [whepUrl, streamToken, getStreamToken, reconnectCount]);


  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        background: "black",
        overflow: "hidden",
      }}
    >
      {/* Video sin controles nativos */}
      <video
        ref={videoRef}
        playsInline
        style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
        onDoubleClick={toggleFullscreen}
      />

      {/* Botón de pantalla completa con imagen */}
      <button
        onClick={toggleFullscreen}
        style={{
          position: "absolute",
          bottom: "10px",
          right: "10px",
          zIndex: 30,
          background: "rgba(0,0,0,0.5)",
          border: "none",
          padding: "6px",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        <img
          src={isFullscreen ? fullscreenExitIcon : fullscreenEnterIcon}
          alt="Fullscreen"
          style={{ width: "32px", height: "32px", display: "block" }}
        />
      </button>

      {/* Overlay: imágenes siempre, nombre solo en fullscreen */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          zIndex: 20,
          pointerEvents: "none",
        }}
      >
          <span
            style={{
              color: "white",
              fontWeight: "bold",
              background: "rgba(0,0,0,0.5)",
              padding: "4px 8px",
              borderRadius: "4px",
              position: "absolute",
              top: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "26px",
            }}
          >
            {userName}
          </span>

        {/* Las imágenes siempre a la izquierda */}
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <img
            src={uvedImg}
            alt="UVED"
            style={{ width: "60px", height: "60px", objectFit: "contain" }}
          />
          <img
            src={policiaImg}
            alt="Policía"
            style={{ width: "60px", height: "60px", objectFit: "contain" }}
          />
        </div>
      </div>


      {/* Loading */}
      {loading && !error && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "rgba(0,0,0,0.6)",
          color: "#fff",
          padding: "1rem 2rem",
          borderRadius: "8px"
        }}>
          cargando...
        </div>
      )}

      {/* Reconectando */}
      {reconnecting && !kicked && (
        <div style={{
          position: "absolute",
          bottom: "60px",
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.65)",
          color: "#fff",
          padding: "0.5rem 1.2rem",
          borderRadius: "8px",
          fontSize: "14px",
          zIndex: 28,
        }}>
          Reconectando… (intento {reconnectAttemptsRef.current + 1}/{MAX_RECONNECT_ATTEMPTS})
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "rgba(255,0,0,0.7)",
          color: "#fff",
          padding: "1rem 2rem",
          borderRadius: "8px"
        }}>
          {error}
        </div>
      )}

      {kicked && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "black",
          zIndex: 25
        }}>
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "white",
            fontSize: "20px",
            background: "rgba(0,0,0,0.6)",
            padding: "1rem 2rem",
            borderRadius: "8px"
          }}>
            Sesión finalizada
          </div>
        </div>
      )}

    </div>
  );
}
